import React, { useState, useEffect } from 'react'
import { 
  getCustomerAddresses, 
  addCustomerAddress, 
  updateCustomerAddress, 
  deleteCustomerAddress,
  searchAddressesByPostcode,
  getAddressById
} from '../../lib/spektrix'
import { getData } from 'country-list'

// Get all countries from country-list package
const countries = getData()

const ManageAddresses = () => {
  const [addresses, setAddresses] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [editingAddress, setEditingAddress] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [message, setMessage] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [postcodeSearch, setPostcodeSearch] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [countrySearch, setCountrySearch] = useState('')
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)

  const emptyAddress = {
    name: '',
    line1: '',
    line2: '',
    line3: '',
    town: '',
    administrativeDivision: '',
    postcode: '',
    country: 'GB'
  }

  const [formData, setFormData] = useState(emptyAddress)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    loadAddresses()
  }, [])

  const loadAddresses = async () => {
    setIsLoading(true)
    try {
      const data = await getCustomerAddresses()
      setAddresses(data)
    } catch (error) {
      console.error('Failed to load addresses:', error)
      setMessage({ type: 'error', text: 'Failed to load addresses. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Address name is required'
    }
    
    if (!formData.line1.trim()) {
      newErrors.line1 = 'Address line 1 is required'
    }
    
    if (!formData.town.trim()) {
      newErrors.town = 'Town/City is required'
    }
    
    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSaving(true)
    setMessage(null)
    
    try {
      if (editingAddress) {
        await updateCustomerAddress(editingAddress.id, formData)
        setMessage({ type: 'success', text: 'Address updated successfully.' })
      } else {
        await addCustomerAddress(formData)
        setMessage({ type: 'success', text: 'Address added successfully.' })
      }
      
      await loadAddresses()
      resetForm()
    } catch (error) {
      console.error('Failed to save address:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to save address. Please try again.' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleEdit = (address) => {
    // Convert country to ISO code if needed
    let countryCode = 'GB'
    if (address.country) {
      if (typeof address.country === 'object') {
        // Country is an object from API
        countryCode = address.country.isoCode || address.country.code || 'GB'
      } else if (typeof address.country === 'string') {
        // Country is a string
        if (address.country.length === 2) {
          countryCode = address.country
        } else {
          // It's a country name, try to find the ISO code
          const countryMatch = countries.find(c => c.name === address.country)
          countryCode = countryMatch ? countryMatch.code : 'GB'
        }
      }
    }
    
    // Handle administrativeDivision - can be string or object
    let adminDivision = ''
    if (address.administrativeDivision) {
      if (typeof address.administrativeDivision === 'object') {
        adminDivision = address.administrativeDivision.name || ''
      } else {
        adminDivision = address.administrativeDivision
      }
    }
    
    setFormData({
      name: address.name || '',
      line1: address.line1 || '',
      line2: address.line2 || '',
      line3: address.line3 || '',
      town: address.town || '',
      administrativeDivision: adminDivision,
      postcode: address.postcode || '',
      country: countryCode
    })
    setEditingAddress(address)
    setShowAddForm(true)
    setErrors({})
    setCountrySearch('')
    setShowCountryDropdown(false)
  }

  const handleDelete = async (addressId) => {
    if (!confirm('Are you sure you want to delete this address?')) {
      return
    }

    setMessage(null)
    
    try {
      await deleteCustomerAddress(addressId)
      setMessage({ type: 'success', text: 'Address deleted successfully.' })
      await loadAddresses()
    } catch (error) {
      console.error('Failed to delete address:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to delete address. Please try again.' })
    }
  }

  const resetForm = () => {
    setFormData(emptyAddress)
    setEditingAddress(null)
    setShowAddForm(false)
    setErrors({})
    setPostcodeSearch('')
    setSearchResults([])
    setShowSearchResults(false)
    setCountrySearch('')
    setShowCountryDropdown(false)
  }

  const handlePostcodeSearch = async () => {
    if (!postcodeSearch.trim()) {
      return
    }

    setIsSearching(true)
    setMessage(null)
    
    try {
      const results = await searchAddressesByPostcode(postcodeSearch)
      setSearchResults(results)
      setShowSearchResults(true)
    } catch (error) {
      console.error('Failed to search addresses:', error)
      setMessage({ type: 'error', text: 'Failed to search addresses. Please try again.' })
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectAddress = async (addressId) => {
    setMessage(null)
    
    try {
      const fullAddress = await getAddressById(addressId)
      // Get country ISO code from the country object or default to GB
      let countryCode = 'GB'
      if (fullAddress.country) {
        if (fullAddress.country.code) {
          countryCode = fullAddress.country.code
        } else if (fullAddress.country.name) {
          // Try to find ISO code from name
          const countryMatch = countries.find(c => c.name === fullAddress.country.name)
          countryCode = countryMatch ? countryMatch.code : 'GB'
        }
      }
      
      setFormData({
        ...formData,
        line1: fullAddress.line1 || '',
        line2: fullAddress.line2 || '',
        line3: fullAddress.line3 || '',
        town: fullAddress.town || '',
        administrativeDivision: fullAddress.administrativeDivision?.name || '',
        postcode: fullAddress.postcode || '',
        country: countryCode
      })
      setShowSearchResults(false)
      setPostcodeSearch('')
    } catch (error) {
      console.error('Failed to get address details:', error)
      setMessage({ type: 'error', text: 'Failed to get address details. Please try again.' })
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <span className="loading loading-spinner loading-md"></span>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-white">Addresses</h2>
        {!showAddForm && (
          <button
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors text-sm"
            onClick={() => {
              resetForm()
              setShowAddForm(true)
            }}
          >
            Add New Address
          </button>
        )}
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'} mb-4`}>
          <span className="text-white">{message.text}</span>
        </div>
      )}

      {showAddForm ? (
        <form onSubmit={handleSubmit} className="space-y-4 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-white">
            {editingAddress ? 'Edit Address' : 'Add New Address'}
          </h3>
          
          <div className="form-control">
            <label className="label">
              <span className="label-text text-gray-300">Address Name *</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Home, Work"
              className={`input input-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500 ${errors.name ? 'input-error' : ''}`}
            />
            {errors.name && (
              <label className="label">
                <span className="label-text-alt text-error">{errors.name}</span>
              </label>
            )}
          </div>

          {/* Postcode Lookup */}
          <div className="form-control">
            <label className="label">
              <span className="label-text text-gray-300">Find Address by Postcode</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={postcodeSearch}
                onChange={(e) => setPostcodeSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePostcodeSearch()}
                placeholder="Enter postcode"
                className="input input-bordered flex-1 bg-gray-700 text-white border-gray-600 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={handlePostcodeSearch}
                disabled={isSearching || !postcodeSearch.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Search Results */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="form-control">
              <label className="label">
                <span className="label-text text-gray-300">Select an address</span>
              </label>
              <select
                className="select select-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500"
                onChange={(e) => e.target.value && handleSelectAddress(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>Choose an address...</option>
                {searchResults.map((result) => (
                  <option key={result.id} value={result.id}>
                    {result.description}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-control">
            <label className="label">
              <span className="label-text text-gray-300">Address Line 1 *</span>
            </label>
            <input
              type="text"
              name="line1"
              value={formData.line1}
              onChange={handleChange}
              className={`input input-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500 ${errors.line1 ? 'input-error' : ''}`}
            />
            {errors.line1 && (
              <label className="label">
                <span className="label-text-alt text-error">{errors.line1}</span>
              </label>
            )}
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text text-gray-300">Address Line 2</span>
            </label>
            <input
              type="text"
              name="line2"
              value={formData.line2}
              onChange={handleChange}
              className="input input-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500"
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text text-gray-300">Address Line 3</span>
            </label>
            <input
              type="text"
              name="line3"
              value={formData.line3}
              onChange={handleChange}
              className="input input-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text text-gray-300">Town/City *</span>
              </label>
              <input
                type="text"
                name="town"
                value={formData.town}
                onChange={handleChange}
                className={`input input-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500 ${errors.town ? 'input-error' : ''}`}
              />
              {errors.town && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.town}</span>
                </label>
              )}
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text text-gray-300">County/State</span>
              </label>
              <input
                type="text"
                name="administrativeDivision"
                value={formData.administrativeDivision}
                onChange={handleChange}
                className="input input-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text text-gray-300">Postcode *</span>
              </label>
              <input
                type="text"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                className={`input input-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500 ${errors.postcode ? 'input-error' : ''}`}
              />
              {errors.postcode && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.postcode}</span>
                </label>
              )}
            </div>

            <div className="form-control relative">
              <label className="label">
                <span className="label-text text-gray-300">Country</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={countrySearch || (formData.country ? countries.find(c => c.code === formData.country)?.name || '' : '')}
                  onChange={(e) => {
                    setCountrySearch(e.target.value)
                    setShowCountryDropdown(true)
                  }}
                  onFocus={() => setShowCountryDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
                  placeholder="Type to search countries..."
                  className="input input-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500 pr-10"
                />
                {(formData.country || countrySearch) && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, country: '' }))
                      setCountrySearch('')
                      setShowCountryDropdown(false)
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
              {showCountryDropdown && (
                <div className="absolute z-10 w-full mt-1 max-h-60 overflow-auto bg-gray-700 border border-gray-600 rounded-lg shadow-lg">
                  {countries
                    .filter(country => 
                      country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                      country.code.toLowerCase().includes(countrySearch.toLowerCase())
                    )
                    .slice(0, 10)
                    .map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        className="w-full px-4 py-2 text-left text-white hover:bg-gray-600 focus:bg-gray-600 focus:outline-none"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, country: country.code }))
                          setCountrySearch('')
                          setShowCountryDropdown(false)
                          // Clear error for this field
                          if (errors.country) {
                            setErrors(prev => ({ ...prev, country: null }))
                          }
                        }}
                      >
                        <span className="font-medium">{country.name}</span>
                        <span className="ml-2 text-gray-400">({country.code})</span>
                      </button>
                    ))
                  }
                  {countries.filter(country => 
                    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                    country.code.toLowerCase().includes(countrySearch.toLowerCase())
                  ).length === 0 && (
                    <div className="px-4 py-2 text-gray-400">No countries found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Saving...
                </>
              ) : (
                editingAddress ? 'Update Address' : 'Add Address'
              )}
            </button>
            <button
              type="button"
              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
              onClick={resetForm}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          {addresses.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p>You haven't added any addresses yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {addresses.map((address) => (
                <div key={address.id} className="card bg-gray-800 border border-gray-700">
                  <div className="card-body">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg text-white">
                          {address.name}
                        </h3>
                        <p className="text-gray-400 mt-2">
                          {address.line1}<br />
                          {address.line2 && <>{address.line2}<br /></>}
                          {address.line3 && <>{address.line3}<br /></>}
                          {address.town}
                          {address.administrativeDivision && (
                            <>, {typeof address.administrativeDivision === 'object' ? address.administrativeDivision.name : address.administrativeDivision}</>
                          )}<br />
                          {address.postcode}<br />
                          {(() => {
                            // Display country name if we have it, otherwise try to look up from ISO code
                            if (address.country) {
                              // Check if country is an object (from API)
                              if (typeof address.country === 'object') {
                                return address.country.name || address.country.isoCode || ''
                              }
                              // Check if it's a string
                              if (typeof address.country === 'string') {
                                if (address.country.length > 2) {
                                  return address.country
                                } else {
                                  const countryMatch = countries.find(c => c.code === address.country)
                                  return countryMatch ? countryMatch.name : address.country
                                }
                              }
                            }
                            return ''
                          })()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="btn btn-sm btn-ghost text-gray-300 hover:text-white hover:bg-gray-700"
                          onClick={() => handleEdit(address)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-ghost text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          onClick={() => handleDelete(address.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ManageAddresses